import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Tooltip from "@mui/material/Tooltip";

export function SidenavItem({
  color,
  icon,
  title,
  active,
  noCollapse,
  collapse,
  route,
  onClick,
  textColor = "white",
  miniSidenav = false,
  ...rest
}) {
  const [open, setOpen] = useState(false);

  const renderIcon = () => {
    if (typeof icon === "string") {
      return <Icon>{icon}</Icon>;
    }
    return icon;
  };

  const handleClick = () => {
    if (collapse && collapse.length > 0) {
      setOpen(!open);
    } else if (onClick) {
      onClick();
    }
  };

  // For menu items with dropdowns
  if (collapse && collapse.length > 0) {
    return (
      <MDBox>
        <Tooltip title={miniSidenav ? title : ""} placement="right">
          <MDBox
            py={1}
            px={miniSidenav ? 1 : 2}
            mx={miniSidenav ? 0.5 : 1}
            mb={0.5}
            onClick={handleClick}
            sx={(theme) => ({
              borderRadius: theme.borders.borderRadius.md,
              background: active ? `rgba(${theme.palette[color].main}, 0.2)` : "transparent",
              transition: "all 300ms linear",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: active
                  ? `rgba(${theme.palette[color].main}, 0.2)`
                  : "rgba(255, 255, 255, 0.1)",
              },
            })}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={miniSidenav ? 0.5 : 1}
              sx={{ cursor: "pointer" }}
            >
              <MDBox display="flex" alignItems="center">
                <MDBox
                  mr={miniSidenav ? 0 : 1.5}
                  bgColor={active ? color : "white"}
                  borderRadius="md"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width={miniSidenav ? "2rem" : "1.5rem"}
                  height={miniSidenav ? "2rem" : "1.5rem"}
                  color={active ? "white" : "dark"}
                  opacity={active ? 1 : 0.8}
                  sx={{
                    transition: "all 300ms linear",
                    "& svg, & .material-icons": {
                      fontSize: miniSidenav ? "1.125rem" : "1rem",
                    },
                  }}
                >
                  {renderIcon()}
                </MDBox>
                {!miniSidenav && (
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    color={textColor}
                    sx={{
                      fontSize: "0.875rem",
                      lineHeight: 0,
                      textShadow: "0px 0px 2px rgba(0,0,0,0.3)",
                      opacity: miniSidenav ? 0 : 1,
                      transition: "opacity 300ms linear",
                    }}
                  >
                    {title}
                  </MDTypography>
                )}
              </MDBox>
              {!miniSidenav && (
                <MDBox
                  sx={{
                    transition: "transform 300ms ease",
                    transform: open ? "rotate(180deg)" : "",
                    mr: -1,
                    "& .material-icons, & .MuiIcon-root": {
                      color: "white !important",
                      fontSize: "1.25rem",
                      fontWeight: "bold",
                    }
                  }}
                >
                  <Icon>expand_more</Icon>
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </Tooltip>

        <Collapse in={open && !miniSidenav} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: miniSidenav ? 0 : 2 }}>
            {collapse.map((item) => (
              <NavLink
                key={item.key}
                to={item.route || "#"}
                style={{ textDecoration: "none" }}
                onClick={onClick}
              >
                <MDBox
                  py={0.5}
                  px={miniSidenav ? 1 : 2}
                  mx={miniSidenav ? 0.5 : 1}
                  mb={0.5}
                  sx={(theme) => ({
                    borderRadius: theme.borders.borderRadius.md,
                    background: item.key === active
                      ? `rgba(${theme.palette[color].main}, 0.2)`
                      : "transparent",
                    transition: "all 300ms linear",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  })}
                >
                  <MDBox display="flex" alignItems="center" p={miniSidenav ? 0.5 : 1}>
                    <MDTypography
                      variant="button"
                      fontWeight="regular"
                      color={textColor}
                      sx={{
                        fontSize: "0.875rem",
                        lineHeight: 0,
                        pl: 0.5,
                        opacity: miniSidenav ? 0 : 1,
                        transition: "opacity 300ms linear",
                      }}
                    >
                      {item.name}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </NavLink>
            ))}
          </List>
        </Collapse>
      </MDBox>
    );
  }

  // For regular menu items (no dropdown)
  return (
    <NavLink to={route || "#"} onClick={onClick} style={{ textDecoration: "none" }} {...rest}>
      <Tooltip title={miniSidenav ? title : ""} placement="right">
        <MDBox
          py={1}
          px={miniSidenav ? 1 : 2}
          mx={miniSidenav ? 0.5 : 1}
          mb={0.5}
          sx={(theme) => ({
            borderRadius: theme.borders.borderRadius.md,
            background: active ? `rgba(${theme.palette[color].main}, 0.2)` : "transparent",
            transition: "all 300ms linear",
            "&:hover": {
              backgroundColor: active
                ? `rgba(${theme.palette[color].main}, 0.2)`
                : "rgba(255, 255, 255, 0.1)",
            },
          })}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={miniSidenav ? 0.5 : 1}
            sx={{ cursor: "pointer" }}
          >
            <MDBox display="flex" alignItems="center">
              <MDBox
                mr={miniSidenav ? 0 : 1.5}
                bgColor={active ? color : "white"}
                borderRadius="md"
                display="flex"
                justifyContent="center"
                alignItems="center"
                width={miniSidenav ? "2rem" : "1.5rem"}
                height={miniSidenav ? "2rem" : "1.5rem"}
                color={active ? "white" : "dark"}
                opacity={active ? 1 : 0.8}
                sx={{
                  transition: "all 300ms linear",
                  "& svg, & .material-icons": {
                    fontSize: miniSidenav ? "1.125rem" : "1rem",
                  },
                }}
              >
                {renderIcon()}
              </MDBox>
              {!miniSidenav && (
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  color={textColor}
                  sx={{
                    fontSize: "0.875rem",
                    lineHeight: 0,
                    textShadow: "0px 0px 2px rgba(0,0,0,0.3)",
                    opacity: miniSidenav ? 0 : 1,
                    transition: "opacity 300ms linear",
                  }}
                >
                  {title}
                </MDTypography>
              )}
            </MDBox>
            {!miniSidenav && !noCollapse && (
              <MDBox
                sx={{
                  transition: "transform 300ms ease",
                  transform: active ? "rotate(180deg)" : "",
                  mr: -1,
                  "& .material-icons, & .MuiIcon-root": {
                    color: "white !important",
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                  }
                }}
              >
                <Icon>expand_more</Icon>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </Tooltip>
    </NavLink>
  );
}

// PropTypes validation
SidenavItem.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  title: PropTypes.string.isRequired,
  active: PropTypes.bool,
  noCollapse: PropTypes.bool,
  collapse: PropTypes.array,
  route: PropTypes.string,
  onClick: PropTypes.func,
  textColor: PropTypes.string,
  miniSidenav: PropTypes.bool,
};

// Default props
SidenavItem.defaultProps = {
  active: false,
  noCollapse: false,
  textColor: "white",
  miniSidenav: false,
};
